import { RefObject, useLayoutEffect, useState } from 'react';
import { format } from 'date-fns';

import Popover from '@/components/Popover';
import styled from '@/styles';
import { TierBoundary } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { tierColor } from './tierColors';
import { clampScore, displayScore } from './score';
import PopoverSurface from './PopoverSurface';
import { PopoverMedal } from './icons';

const Content = styled(PopoverSurface, {
  padding: '16px 24px',
  width: '448px',
  // GRAVY-10347: never wider than the viewport, so on a very narrow window/high
  // zoom the popover shrinks to fit instead of running off an edge (the JS
  // clamp below keeps it inside the card; this is the last-resort width guard).
  maxWidth: 'calc(100vw - 16px)',
});
// Figma 40013-12830: the compact "Joined <date>" tooltip a NEW WORKER's badge
// opens (in place of the tier bar). Same PopoverSurface chrome, hug width, 12x16
// padding — reuses the tier popover's anchor positioning below.
const TooltipContent = styled(PopoverSurface, {
  padding: '12px 16px',
  maxWidth: 'calc(100vw - 16px)',
});
const JoinedLine = styled('div', {
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1.4,
  color: dependabilityV2Colors.depInk,
  whiteSpace: 'nowrap',
});
const Title = styled('div', {
  fontWeight: 700,
  fontSize: '16px',
  color: dependabilityV2Colors.depInk,
  marginBottom: '4px',
});
const Subtitle = styled('div', {
  fontSize: '14px',
  fontWeight: 400,
  color: dependabilityV2Colors.depInk,
  marginBottom: '16px',
});
// Medal artwork beside the tier text, per the popover frame (37858-31304)
const TierSummary = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
});
const TierName = styled('div', {
  fontSize: '14px',
  fontWeight: 700,
  color: dependabilityV2Colors.depInk,
});
const TierScore = styled('div', {
  fontSize: '11px',
  fontWeight: 400,
  color: dependabilityV2Colors.depInk,
});
// Directional labels above the tier bar (tooltip.png): "Needs improvement" on
// the low end, "Most reliable" on the high end — muted meta-gray, normal weight.
const RangeLabels = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '8px',
  fontSize: '12px',
  fontWeight: 400,
  color: dependabilityV2Colors.depMetaText,
});
const Bar = styled('div', {
  position: 'relative',
  display: 'flex',
  height: '6px',
  borderRadius: '100px',
  overflow: 'visible',
  marginBottom: '8px',
});
const Segment = styled('div', { height: '6px' });
const Marker = styled('div', {
  position: 'absolute',
  top: '-4px',
  width: '13px',
  height: '13px',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.2)',
  transform: 'translateX(-50%)',
});
const Labels = styled('div', {
  display: 'flex',
});
const LabelBlock = styled('div', { flex: 1, textAlign: 'right' });
const LabelName = styled('div', {
  fontSize: '12px',
  fontWeight: 600,
  color: dependabilityV2Colors.depInk,
});
const LabelRange = styled('div', {
  fontSize: '11px',
  fontWeight: 400,
  color: dependabilityV2Colors.depInk,
});

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  boundaries: TierBoundary[];
  score: number;
  tier: string;
  completedShifts: number;
  scoringMode: 'activation' | 'reliability';
  // Figma 40013-12830: when set (an activation-mode "new worker" with a known
  // join date), the popover collapses to a compact "Joined <date>" tooltip in
  // place of the tier bar. ISO8601; falls back to the tier view when absent.
  newWorkerJoinedAt?: string | null;
  justify?: 'left' | 'right';
  // GRAVY-10347: the element the popover is anchored to (the tier pill's
  // wrapper). When provided, the popover is positioned with a measured,
  // card-clamped `absolute` placement (relative to this anchor) so it can never
  // cover the header or slide under the sidebar at any width/zoom, and so it
  // scrolls with the page rather than following the scroll. Omitted (e.g. in
  // unit tests) it falls back to the static placement.
  anchorRef?: RefObject<HTMLElement | null>;
}

// "Joined Aug 14, 2026" — same date-fns 'MMM d, yyyy' formatting as the
// activation breakdown's account-created line; guards a malformed ISO string.
const formatJoined = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'MMM d, yyyy');
};

const rangeLabel = (b: TierBoundary, isFirst: boolean, isLast: boolean) => {
  if (isFirst) return `<${b.max + 1}`;
  if (isLast) return `${b.min}+`;
  return `${b.min}–${b.max}`;
};

// Segments render at EQUAL widths (not proportional to their score span), so
// the marker is placed piecewise: which segment the tier occupies, then the
// score's fractional position within that segment. The denominator spans to
// the NEXT tier's min (max - min + 1): a 2-decimal score like 89.5 is still
// Gold and must land inside the gold segment, not on the platinum boundary.
const markerLeft = (
  boundaries: TierBoundary[],
  score: number,
  tier: string,
): number => {
  const n = boundaries.length;
  if (n === 0) return 0;
  const clamped = clampScore(score);
  let idx = boundaries.findIndex((b) => b.tier === tier);
  if (idx === -1)
    idx = boundaries.findIndex((b) => clamped >= b.min && clamped <= b.max);
  if (idx === -1) idx = clamped < boundaries[0].min ? 0 : n - 1;
  const b = boundaries[idx];
  const span = Math.max(1, b.max - b.min + 1);
  const frac = Math.max(0, Math.min(1, (clamped - b.min) / span));
  return ((idx + frac) / n) * 100;
};

const segmentRadius = (i: number, count: number) => {
  if (i === 0) return '100px 0 0 100px';
  if (i === count - 1) return '0 100px 100px 0';
  return '0';
};

const TierPopover = ({
  isOpen,
  onRequestClose,
  boundaries,
  score,
  tier,
  completedShifts,
  scoringMode,
  newWorkerJoinedAt,
  justify = 'left',
  anchorRef,
}: Props) => {
  const tierLabel = boundaries.find((b) => b.tier === tier)?.label || tier;
  const joined = newWorkerJoinedAt ? formatJoined(newWorkerJoinedAt) : '';

  // GRAVY-10347: the shared Popover positions with static CSS only (align/justify
  // offsets) and has no collision handling — so `align="above"` covered the page
  // header and `justify="right"` slid the 348px popover left under the fixed
  // sidebar. Dropping it straight below the pill (the first fix) then floated it
  // over the STAT CARDS, and flipping above floated it over the badges/ring. The
  // pill is embedded top-right of the card with content on every side, so no
  // single static choice is safe. When an anchor is supplied we measure the pill,
  // the card, the sibling content (stat cards / badge row / score ring) and the
  // viewport, evaluate a set of candidate placements (below / to the right /
  // above / to the left of the pill), and pick the one that overlaps the sibling
  // content least while never crossing the card's top or left edge (which is
  // what keeps it off the page header and the nav rail). The chosen placement is
  // measured ONCE at open (and on resize) and stored relative to the anchor, so
  // the absolute-positioned popover then scrolls with the page — it is
  // deliberately NOT recomputed on scroll, so it stays where it was opened
  // instead of following the scroll.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchorEl = anchorRef?.current;
    if (!isOpen || !anchorEl) {
      setPos(null);
      return undefined;
    }

    const compute = () => {
      const box = document.querySelector<HTMLElement>(
        '[data-testid="tier-popover"]',
      );
      if (!anchorEl || !box) return;

      const PAD = 8;
      const GAP = 6;
      const a = anchorEl.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      const cardEl = anchorEl.closest<HTMLElement>(
        '[data-testid="dependability-v2-card"]',
      );
      const card = cardEl?.getBoundingClientRect();
      const vw = window.innerWidth;
      const width = b.width || 448;
      const height = b.height || 0;

      // The pill sits directly above the "See breakdown" CTA in the score
      // column, so a below/side placement covered it (and on focus it covered
      // the next focusable). Place the popover ABOVE the pill — its bottom edge
      // GAP above the pill's top — so it can never cover the CTA that sits below
      // the pill. Clamp only to the viewport top (PAD), NOT the card top, so it
      // has room to sit fully above the pill. Kept right-aligned to the pill and
      // inside the card horizontally.
      const clampXCard = (x: number) => {
        const cardMin = Math.max(PAD, (card ? card.left : 0) + PAD);
        const cardMax = (card ? card.right : vw) - PAD - width;
        const inCard =
          cardMax >= cardMin
            ? Math.min(Math.max(x, cardMin), cardMax)
            : cardMin;
        return Math.max(PAD, Math.min(inCard, vw - PAD - width));
      };

      const top = Math.max(PAD, a.top - GAP - height);
      const left = clampXCard(a.right - width);

      // Store the placement RELATIVE TO THE ANCHOR (rendered position:absolute),
      // so once opened the popover travels WITH the page on scroll instead of
      // following it. Measured once at open (and on resize), NOT on scroll.
      setPos({
        top: Math.round(top - a.top),
        left: Math.round(left - a.left),
      });
    };

    compute();
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('resize', compute);
    };
  }, [isOpen, anchorRef]);

  return (
    <Popover
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      // The compact "Joined <date>" tooltip hugs its content (auto = no min-width);
      // the tier popover keeps the wider lg surface.
      size={scoringMode === 'activation' ? 'auto' : 'lg'}
      justify={justify}
      // the frame shows the popover floating above the pill
      align="above"
      {...(pos
        ? {
            css: { position: 'absolute', margin: 0 },
            style: {
              top: pos.top,
              left: pos.left,
              right: 'auto',
              bottom: 'auto',
            },
          }
        : {})}
    >
      {scoringMode === 'activation' ? (
        // A new (activation) worker's badge opens ONLY a compact "Joined <date>"
        // tooltip — the tier bar does not apply until they earn a shift-based
        // tier. Falls back to "New worker" if the join date has not deployed yet.
        <TooltipContent data-testid="tier-popover">
          <JoinedLine data-testid="new-worker-tooltip">
            {joined ? `Joined ${joined}` : 'New worker'}
          </JoinedLine>
        </TooltipContent>
      ) : (
        <Content data-testid="tier-popover">
          {/* The full popover (tier bar) is reliability-only now — activation
              workers are handled by the compact tooltip above. */}
          <Title>Dependability tier</Title>
          <Subtitle>{`Based on ${completedShifts} shifts`}</Subtitle>
          <TierSummary>
            <PopoverMedal tier={tier} color={tierColor(tier)} />
            <div>
              <TierName>{`${tierLabel} tier`}</TierName>
              <TierScore>{`Score: ${displayScore(score)}`}</TierScore>
            </div>
          </TierSummary>
          <RangeLabels data-testid="tier-range-labels">
            <span>Needs improvement</span>
            <span>Most reliable</span>
          </RangeLabels>
          <Bar>
            {boundaries.map((b, i) => (
              <Segment
                key={b.tier}
                css={{
                  width: `${100 / boundaries.length}%`,
                  backgroundColor: tierColor(b.tier),
                  // the Marker is Bar's last child, so :last-child can't round
                  // the final segment — set end caps explicitly by index
                  borderRadius: segmentRadius(i, boundaries.length),
                }}
              />
            ))}
            <Marker
              data-testid="tier-popover-marker"
              style={{
                left: `${markerLeft(boundaries, score, tier)}%`,
                border: `1px solid ${tierColor(tier)}`,
              }}
            />
          </Bar>
          <Labels>
            {boundaries.map((b, i) => (
              <LabelBlock key={b.tier}>
                <LabelName>{b.label}</LabelName>
                <LabelRange>
                  {rangeLabel(b, i === 0, i === boundaries.length - 1)}
                </LabelRange>
              </LabelBlock>
            ))}
          </Labels>
        </Content>
      )}
    </Popover>
  );
};

export default TierPopover;
